import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useLocation } from 'react-router-dom';
import { getColumnSortOrder } from 'src/components/table/columnFormatHelpers';
import { DEFAULT_PAGINATION } from 'src/domain/commonConst';
import { compareWithLocale } from 'src/domain/compareWithLocale';
import { MemoizedDatetimePopover } from '../../../components/datetimePopover/datetimePopover';
import { PageFooter } from '../../../components/PageFooter';
import { PageHeader } from '../../../components/PageHeader';
import { ProgramTag } from '../../../components/ProgramTag';
import { SearchInput } from '../../../components/searchInput/searchInput';
import { SidebarGrid } from '../../../components/Sidebar/SidebarGrid';
import { CustomizableColumnType, CustomizableTable } from '../../../components/table/CustomizableTable';
import { CustomizableTableContextProvider } from '../../../components/table/CustomizableTableContext';
import { WithEmptyDataTable } from '../../../components/table/withEmptyDataTable';
import { useDocumentTitle } from '../../../components/useDocumentTitle';
import { exportTrackingAlerts } from '../../../domain/alert-track';
import { useAlertTrackListQuery } from '../../../domain/alert-track/queries';
import { ITrackingAlert } from '../../../domain/event/interface';
import { ALERT_TRACKING_PERIOD_OPTIONS } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { toUsefulParams } from '../../toUsefulParams';
import { usePageLocation } from '../../usePageState';
import { EventTrackingSidebar } from '../sidebar/EventTrackingSidebar';
import { IEventTrackingPageState, useEventTrackingSidebarFiltersContext } from '../sidebar/EventTrackingSidebarFiltersContext';
import { useEventTrackingSidebar } from '../sidebar/useEventTrackingSidebar';
import { TrackingAlertStatusTag } from '../../../components/TrackingAlertStatusTag';
import { EventViewSwitcher } from '../components/EventViewSwitcher';

const DEFAULT_SORTER = {
    field: 'event_datetime_start',
    order: 'descend',
};

export const EventTrackingView = () => {
    const { companyId } = useOutletContext<any>();

    useDocumentTitle('Events - Tracking View');

    const location = useLocation();
    const { notification } = App.useApp();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: IEventTrackingPageState = queryToState(location.search);

    const [exportLoading, setExportLoading] = useState(false);

    const { layoutPaddingLeft, sidebarCollapsed, setSidebarCollapsed } = useEventTrackingSidebar();

    const { filters, handleDefaultFiltersApply } = useEventTrackingSidebarFiltersContext();

    const listQuery = {
        filter: pageState.filter,
        sorter: pageState?.sorter ?? DEFAULT_SORTER,
        companyId,
        search: pageState?.search,
    };

    const {
        isFetching,
        isError,
        error,
        data: trackingAlerts,
    } = useAlertTrackListQuery(listQuery, {
        refetchOnMount: 'always',
        keepPreviousData: true,
        staleTime: 5 * 60 * 1000,
    });

    const meta = {
        total: trackingAlerts?.length || 0,
        limit: pageState.pagination?.pageSize || DEFAULT_PAGINATION.pageSize,
        offset: ((pageState.pagination?.current || 1) - 1) * (pageState.pagination?.pageSize || DEFAULT_PAGINATION.pageSize),
    };

    if (isError) {
        notification.error({
            key: 'alert-track-list-error',
            message: error.message || 'Cannot load alert tracks!',
        });
    }

    const columns: CustomizableColumnType<ITrackingAlert>[] = [
        {
            key: 'company',
            title: 'Company',
            dataIndex: 'company',
            width: 200,
        },
        {
            width: 200,
            title: 'User',
            dataIndex: 'user_ids',
            key: 'user_ids',
            sorter: (a, b) => compareWithLocale(a.user?.fname?.concat(' ').concat(a.user?.lname), b.user?.fname?.concat(' ').concat(b.user?.lname)),
            ...getColumnSortOrder('user_ids', pageState.sorter),
            render: (text, record) => `${record.user?.fname} ${record.user?.lname}`,
        },
        {
            key: 'email',
            title: 'Email',
            dataIndex: ['user', 'email'],
            width: 200,
        },
        {
            key: 'phone',
            title: 'Phone',
            dataIndex: ['user', 'phone'],
            width: 200,
        },
        {
            key: 'event_id',
            title: 'Event',
            dataIndex: 'event_datetime_start',
            render: (startDate: string) => (startDate ? <MemoizedDatetimePopover timestamp={new Date(startDate).valueOf()} /> : '-'),
            sorter: (a, b) => compareWithLocale(a.event_datetime_start, b.event_datetime_start),
            ...getColumnSortOrder('event_datetime_start', pageState.sorter),
            width: 160,
        },
        {
            key: 'alert_status',
            title: 'Notification Type',
            dataIndex: 'alert_statuses',
            width: 120,
            ellipsis: true,
            render: (text, record) => record.alert_status,
        },
        {
            key: 'event_status',
            title: 'Event Status',
            dataIndex: 'event_statuses',
            width: 120,
            ellipsis: true,
            render: (text, record) => record.event_status,
        },
        {
            key: 'event_programs',
            title: 'Program',
            dataIndex: 'event_programs',
            ellipsis: { showTitle: false },
            render: (text: string, record: ITrackingAlert) =>
                record?.event_programs?.map((name: string, i: number) => <ProgramTag programName={name} key={i} />),
            width: 200,
        },
        {
            key: 'sentToEmailAt',
            title: 'Sent to Email',
            dataIndex: 'sentToEmailAt',
            render: (text, record) => !!record.sentToEmailAt && <MemoizedDatetimePopover timestamp={new Date(record.sentToEmailAt).valueOf()} />,
            width: 160,
        },
        {
            key: 'sentToPhoneAt',
            title: 'Sent to Phone',
            dataIndex: 'sentToPhoneAt',
            render: (text, record) => !!record.sentToPhoneAt && <MemoizedDatetimePopover timestamp={new Date(record.sentToPhoneAt).valueOf()} />,
            width: 160,
        },
        {
            key: 'is_opened',
            title: 'Opened',
            dataIndex: 'is_opened',
            filterMultiple: false,
            render: (text, record) => !!record.openedEmailAt && <MemoizedDatetimePopover timestamp={new Date(record.openedEmailAt).valueOf()} />,
        },
        {
            key: 'participation_status',
            title: 'Participation',
            dataIndex: 'participation_status',
            render: (text, record) => <TrackingAlertStatusTag trackingAlert={record} />,
            width: 100,
        },
    ];

    const exportTrackingAlertsAction = async () => {
        setExportLoading(true);
        try {
            await exportTrackingAlerts({
                filter: pageState.filter,
                sorter: pageState.sorter || DEFAULT_SORTER,
                search: pageState.search,
                companyId,
            });
        } catch (error: any) {
            notification.error({
                key: 'events-tracking-alerts-export-error',
                message: error.message || 'Cannot export tracking alerts!',
            });
        }
        setExportLoading(false);
    };

    const onTableChange = (pagination: any, filter: any, sorter: any) => {
        const newFilter = {
            ...pageState.filter,
            ...filter,
        };

        const preparedParams = toUsefulParams({ pagination, filter: newFilter, sorter });

        setPageQuery({ ...pageState, ...preparedParams });
    };

    const handleSearch = (value: string) => {
        if (value === pageState.search) {
            setPageQuery({
                ...pageState,
                pagination: { ...pageState.pagination, current: DEFAULT_PAGINATION.current },
            });
            return;
        }

        setPageQuery({ ...pageState, search: value });
    };

    // if period filter is not set, apply default value
    useEffect(() => {
        if (!!filters.period) return;

        handleDefaultFiltersApply({
            ...filters,
            period: ALERT_TRACKING_PERIOD_OPTIONS[0].value,
        });
    }, [filters, filters.period, handleDefaultFiltersApply]);

    return (
        <>
            <CustomizableTableContextProvider configKey="eventTracking" columns={columns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    <EventTrackingSidebar
                        collapsed={sidebarCollapsed}
                        onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                        totalRecords={meta?.total}
                        onFilterChange={() => {}}
                        gridComponent={<SidebarGrid />}
                    />

                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader
                            pageTitle="Events"
                            extra={<EventViewSwitcher />}
                            actions={[
                                <SearchInput key="alert-event-search" onSearch={handleSearch} defaultValue={pageState?.search} />,
                                <Button
                                    loading={exportLoading}
                                    size="large"
                                    key="download-csv"
                                    onClick={exportTrackingAlertsAction}
                                    icon={<DownloadOutlined />}
                                >
                                    Download CSV
                                </Button>,
                            ]}
                        />
                        <WithEmptyDataTable condition={isFetching} emptyDataDescription="No Data for Specified Search">
                            <CustomizableTable<ITrackingAlert>
                                size="small"
                                rowKey={makeRowKeyClosure('tracking')}
                                sticky={{ offsetHeader: 62 }}
                                showHiddenColumnsCount={true}
                                dataSource={trackingAlerts}
                                loading={isFetching}
                                onChange={onTableChange}
                                meta={meta}
                            />
                        </WithEmptyDataTable>
                        <PageFooter />
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>
        </>
    );
};

function makeRowKeyClosure(prefix: string) {
    let rowIndex = 0;
    return () => `${prefix}-${rowIndex++}`;
}
